import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ErrorHandler, Injectable } from '@angular/core';

import { EnvironmentService } from './environment.service';

/**
 * ErrorHandlerService is used to catch Angular and global error on the client side.
 * The Angular side of the handler is declared in the ngModule as the ErrorHandler provider
 * The catched error is then formatted and sent to the Http Server to be logged in Kibana
 */
@Injectable({
    providedIn: 'root',
})
export class ErrorHandlerService implements ErrorHandler {
    private static readonly ANGULAR_SCOPE = 'angular';
    private static readonly GLOBAL_SCOPE = 'global';

    private static readonly STACK_LIMIT = 10;
    private static readonly DEFAULT_ENV = {
        LOAD: { GLOBAL: 5, ANGULAR: 5 },
        ENDPOINT: '/errorClient',
    };

    public constructor(
        private httpService: HttpClient,
        private environmentService: EnvironmentService,
    ) {
        window.onerror = (message, source, line, col, error) => {
            if (!error) {
                const messageStr = typeof message === 'string' ? message : `Event ${message.type}`;
                error = { name: `nullGlobalError`, message: messageStr, stack: `${source}:${line}:${col}` };
            }
            this.formatAndSendLog(error, ErrorHandlerService.GLOBAL_SCOPE);
        };
    }

    /**
     * Method from ErrorHandler Interface, used by Angular for handling errors
     * @param {Error} error
     */
    public handleError(error: Error): void {
        this.formatAndSendLog(error, ErrorHandlerService.ANGULAR_SCOPE);
    }

    /**
     * Method to log custom error passed as parameter to the function
     * @param {Error} error
     * @param {string} scope
     */
    public logCustomError(error: Error, scope: string): void {
        this.formatAndSendLog(error, scope);
    }

    /**
     * Prepare and send request to the server in order to log the error on the server side
     * The quantity of logs to be sent can be modified in the ERROR_TRACKER.LOAD node of env.json
     * @param {Error|null} error Error object from Angular handler or window.onerror listener
     * @param {string} scope string to define which scope the error come from (angular or global for now)
     * @private
     */
    private formatAndSendLog(error: Error | null, scope: string): void {
        const env = this.environmentService.instant()?.API?.ERROR_TRACKER || ErrorHandlerService.DEFAULT_ENV;
        if (env?.LOAD) {
            const load = env.LOAD[scope.toUpperCase()] || 0;
            if (Math.floor(Math.random() * 100) < load) {
                if (error === null) {
                    error = new Error('error is null');
                }
                const stack = (error?.stack || '').split('\n').splice(0, ErrorHandlerService.STACK_LIMIT).join('\n');
                const headers = new HttpHeaders()
                    .set('X-Error-Tracking-Name', this.escape(error?.name || 'Unknown error'))
                    .set('X-Error-Tracking-Message', this.escape(error?.message || ''))
                    .set('X-Error-Tracking-Stack', this.escape(stack))
                    .set('X-Error-Tracking-Scope', this.escape(scope));

                this.httpService.get(env.ENDPOINT, { headers }).subscribe({
                    error: () => null, // avoid infinite loop, error -> log -> error -> log
                });
            }
        }

        // global scope errors are already logged in console by the browser
        if (env?.DISABLE_CONSOLE !== true && scope === ErrorHandlerService.ANGULAR_SCOPE) {
            // eslint-disable-next-line no-console
            console.error(error);
        }
    }

    /**
     * Protect the string to avoid sending line feed or carriage return in header
     * Based on JSON.stringify, but remove the first and last double quotes
     * @param {string} value
     * @returns string
     * @private
     */
    private escape(value: string): string {
        return JSON.stringify(value).replace(/^"|"$/g, '');
    }
}
